/*
 * KANBAN DATA MODELS
 * 
 * Type definitions for Kanban board configuration, including:
 * - KanbanBoard: Database entity for persisted boards
 * - KanbanConfig: Full board configuration with filters and lane visibility
 * - FilterGroup: Nested filter groups with AND/OR logic
 * - FilterRule: Individual filter rules with field, operator, value
 * - Filter operators mapped by field type
 * 
 * Used by:
 * - Kanban.tsx: Main page state management
 * - KanbanEditor.tsx: Configuration panel
 * - KanbanBoard.tsx: Rendering and filtering
 * - kanbanService.ts: API service layer
 */

import { type FormField } from '../components/common/FormField';

// Database entity for persisted Kanban boards
export interface KanbanBoard {
  id: number;
  userId: number | null;
  schemaId: number;
  name: string;
  config: string; // JSON string of KanbanConfig
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number;
  // Joined fields
  schemaName?: string;
  entityType?: string;
  createdByName?: string;
}

// Filter operator types
export type FilterOperator =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'equals'
  | 'greater_than'
  | 'less_than'
  | 'before'
  | 'after'
  | 'is_any_of';

// Individual filter rule
export interface FilterRule {
  id: string;
  field: string;
  operator: FilterOperator;
  value: string | string[];
}

// Filter group with AND/OR logic and nested groups
export interface FilterGroup {
  operator: 'AND' | 'OR';
  rules: FilterRule[];
  groups: FilterGroup[];
}

// Full Kanban configuration
export interface KanbanConfig {
  id: string;
  name: string;
  entityType: 'accounts' | 'contacts';
  typeId: string;           // Schema ID (e.g., "account-1")
  typeName: string;
  groupField: string;       // Field name to group by
  groupFieldLabel: string;
  visibleLanes: string[];   // Empty = show all
  includeUnassigned: boolean;
  filters: FilterGroup;
  createdAt: string;
  updatedAt: string;
}

// Legacy SavedKanban format for migration
export interface LegacySavedKanban {
  id: string;
  name: string;
  typeId: string;
  typeName: string;
  fieldName: string;
  fieldLabel: string;
  entityType: number; // 1 = contacts, 2 = accounts
  createdAt: string;
}

// Operators available for each field type
export const OPERATORS_BY_FIELD_TYPE: Record<FormField['type'], FilterOperator[]> = {
  text: ['is', 'is_not', 'contains', 'starts_with', 'ends_with'],
  email: ['is', 'is_not', 'contains', 'starts_with', 'ends_with'],
  tel: ['is', 'is_not', 'contains', 'starts_with', 'ends_with'],
  url: ['is', 'is_not', 'contains', 'starts_with', 'ends_with'],
  textarea: ['is', 'is_not', 'contains', 'starts_with', 'ends_with'],
  password: ['is', 'is_not'],
  select: ['is', 'is_not', 'is_any_of'],
  number: ['equals', 'greater_than', 'less_than'],
  date: ['is', 'before', 'after'],
  boolean: ['is']
};

// Human-readable labels for operators
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: 'is',
  is_not: 'is not',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  equals: 'equals',
  greater_than: 'greater than',
  less_than: 'less than',
  before: 'before',
  after: 'after',
  is_any_of: 'is any of'
};

/**
 * Create an empty KanbanConfig with default values
 */
export function createEmptyConfig(): KanbanConfig {
  return {
    id: '',
    name: '',
    entityType: 'accounts',
    typeId: '',
    typeName: '',
    groupField: '',
    groupFieldLabel: '',
    visibleLanes: [],
    includeUnassigned: true,
    filters: {
      operator: 'AND',
      rules: [],
      groups: []
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

/**
 * Create a new filter rule with unique ID
 */
export function createFilterRule(field: string = '', operator: FilterOperator = 'is', value: string | string[] = ''): FilterRule {
  return {
    id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    field,
    operator,
    value
  };
}

/**
 * Create an empty filter group
 */
export function createFilterGroup(operator: 'AND' | 'OR' = 'AND'): FilterGroup {
  return {
    operator,
    rules: [],
    groups: []
  };
}

/**
 * Migrate legacy SavedKanban to new KanbanConfig format
 */
export function migrateFromLegacy(legacy: LegacySavedKanban): KanbanConfig {
  return {
    id: legacy.id,
    name: legacy.name,
    entityType: legacy.entityType === 2 ? 'accounts' : 'contacts',
    typeId: legacy.typeId,
    typeName: legacy.typeName,
    groupField: legacy.fieldName,
    groupFieldLabel: legacy.fieldLabel,
    visibleLanes: [], // Empty = show all lanes
    includeUnassigned: true,
    filters: {
      operator: 'AND',
      rules: [],
      groups: []
    },
    createdAt: legacy.createdAt,
    updatedAt: legacy.createdAt
  };
}

/**
 * Check if a saved kanban is in legacy format
 */
export function isLegacyFormat(saved: unknown): saved is LegacySavedKanban {
  if (!saved || typeof saved !== 'object') return false;
  const obj = saved as Record<string, unknown>;
  // Legacy format has 'fieldName' instead of 'groupField' and entityType as number
  return 'fieldName' in obj && typeof obj.entityType === 'number';
}

/**
 * Get operators available for a given field type
 */
export function getOperatorsForFieldType(fieldType: FormField['type']): FilterOperator[] {
  return OPERATORS_BY_FIELD_TYPE[fieldType] || OPERATORS_BY_FIELD_TYPE.text;
}
